/**
 * NEEDS A PERSON — everybody waiting on a human, and who has them.
 *
 * Anthony, 2026-09-28, for WeRV: "a queue for problem people who want to
 * talk to a human". Before this, a hand-off from the text assistant rang
 * the bell once and went into the lead's notes, and the question the desk
 * actually has, "who is still waiting on one of us", had no screen.
 *
 * One card per customer. The assistant opens it when it hands somebody
 * over (an angry customer, a refund, an injury, "can I talk to a real
 * person"), and anybody at the shop can open one by hand. The assistant
 * never closes one, not even when it picks the conversation back up after
 * fifteen minutes: carrying on texting is not the same as somebody having
 * rung them. A person presses Done.
 *
 * Reads and writes go through /api/queue (server/routes/human-queue.ts),
 * which checks who is asking from their own token. The rules for what a
 * valid card is are in src/domain/humanqueue.ts, shared with the server.
 */

import { api, db } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import { dialable, parseManual, queueOrder, waitingFor, type QueueItem } from "../domain/humanqueue.ts";

type Answer = { me?: string; may_write?: boolean; open?: QueueItem[]; closed_today?: QueueItem[]; error?: string; missing?: boolean };

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

async function call(path: string, body?: unknown): Promise<{ ok: boolean; out: Answer & { item?: QueueItem } }> {
  const r = await fetch(api(path), {
    method: body === undefined ? "GET" : "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const out = await r.json().catch(() => ({})) as Answer & { item?: QueueItem };
  return { ok: r.ok, out };
}

/**
 * How many are waiting, for the count beside the menu link. Read straight
 * from the table under the signed-in person's own row security, which
 * scopes it to their shop. Zero on any failure, including the table not
 * existing yet: a badge is not the place to report an outage, and the page
 * itself says so properly.
 */
export async function openQueueCount(): Promise<number> {
  try {
    const sb = await db();
    const { count, error } = await sb.from("human_queue")
      .select("id", { count: "exact", head: true }).is("closed_at", null);
    return error ? 0 : count ?? 0;
  } catch { return 0; }
}

let role = "";

export async function queuePage(r: string): Promise<void> {
  role = r;
  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Needs a person</h1>
      <p>Customers who asked for a human, or who the text assistant handed over.
         Ring or text them, press I've got it so nobody doubles up, then Done.</p></header>
    <div class="card" id="hq-open"><div class="loading">Reading&hellip;</div></div>
    <div class="card" id="hq-closed" hidden></div>
    <div class="card" id="hq-add-card" hidden>
      <div class="card-head">Add someone</div>
      <div class="hq-add">
        <label class="field"><span>Phone</span>
          <input id="hq-phone" type="tel" inputmode="tel" autocomplete="off" placeholder="951 555 0100"></label>
        <label class="field"><span>Name, if you have it</span>
          <input id="hq-name" type="text" autocomplete="off"></label>
        <label class="field"><span>Why they need a person</span>
          <input id="hq-reason" type="text" autocomplete="off" placeholder="Wants to change their dates"></label>
        <label class="check"><input type="checkbox" id="hq-urgent"> Urgent</label>
        <button class="btn primary" id="hq-add">Add to the list</button>
        <p class="msg bad" id="hq-add-msg" hidden></p>
      </div>
    </div>
  </div>`);
  await draw();
}

function card(it: QueueItem, me: string, mayWrite: boolean): string {
  const who = it.customer_name || dialable(it.phone10) || "No name or number";
  const phone = it.phone10 ? `+1${it.phone10}` : "";
  const mine = it.claimed_by && it.claimed_by === me;
  const holder = it.claimed_by ? (mine ? "You have it" : `${it.claimed_name || "Somebody"} has it`) : "Nobody has it yet";
  return `
    <div class="row hq-row${it.urgent ? " hq-urgent" : ""}" data-id="${esc(it.id)}">
      <div class="row-main">
        <div class="row-title"><span class="num">${esc(who)}</span>
          ${it.urgent ? `<span class="pill hq-pill-urgent">Urgent</span>` : ""}
          ${it.source === "manual" ? `<span class="pill">Added by hand</span>` : ""}</div>
        ${it.customer_name && it.phone10 ? `<div class="row-sub">${esc(dialable(it.phone10))}</div>` : ""}
        <div class="row-sub">${esc(it.reason)}</div>
        ${it.last_message ? `<div class="row-note">They said: &ldquo;${esc(it.last_message)}&rdquo;</div>` : ""}
        <div class="row-note">${esc(waitingFor(it.opened_at))}<span class="dot">&middot;</span>${esc(holder)}</div>
        <div class="hq-actions">
          ${phone ? `<a class="btn sm" href="tel:${esc(phone)}">Call</a>
                     <a class="btn sm" href="sms:${esc(phone)}">Text</a>` : ""}
          ${mayWrite ? `
            ${mine ? `<button class="btn ghost sm" data-release="${esc(it.id)}">Release</button>`
              : `<button class="btn sm" data-claim="${esc(it.id)}">I've got it</button>`}
            ${it.claimed_by && !mine ? `<button class="btn ghost sm" data-release="${esc(it.id)}">Release</button>` : ""}
            <button class="btn primary sm" data-done="${esc(it.id)}">Done</button>` : ""}
        </div>
        <div class="hq-done" data-done-box="${esc(it.id)}" hidden>
          <label class="field"><span>What happened, if you want to say</span>
            <input type="text" data-note="${esc(it.id)}" autocomplete="off" placeholder="Rang her, dates moved"></label>
          <button class="btn primary sm" data-close="${esc(it.id)}">Close it</button>
          <button class="btn ghost sm" data-cancel="${esc(it.id)}">Not yet</button>
        </div>
        <p class="msg bad" data-msg="${esc(it.id)}" hidden></p>
      </div>
    </div>`;
}

function closedRow(it: QueueItem): string {
  const who = it.customer_name || dialable(it.phone10) || "No name or number";
  return `<div class="row"><div class="row-main">
      <div class="row-title"><span class="num">${esc(who)}</span></div>
      <div class="row-sub">${esc(it.reason)}</div>
      <div class="row-note">Done${it.claimed_name ? ` by ${esc(it.claimed_name)}` : ""}${
        it.close_note ? `: ${esc(it.close_note)}` : ""}</div>
    </div></div>`;
}

async function draw(): Promise<void> {
  const host = document.getElementById("hq-open");
  const closedHost = document.getElementById("hq-closed");
  const addCard = document.getElementById("hq-add-card");
  if (!host) return;
  let res: { ok: boolean; out: Answer };
  try { res = await call("/api/queue"); }
  catch { host.innerHTML = `<div class="empty">Could not reach the server. Try again in a moment.</div>`; return; }
  if (!res.ok) {
    host.innerHTML = `<div class="empty">${esc(res.out.error ?? "Could not read the list.")}</div>`;
    return;
  }
  const me = res.out.me ?? "";
  const mayWrite = res.out.may_write !== false && role !== "viewer";
  const open = [...(res.out.open ?? [])].sort(queueOrder);
  const closed = res.out.closed_today ?? [];

  host.innerHTML = open.length
    ? `<div class="card-head">${open.length} waiting on a person</div>${open.map((it) => card(it, me, mayWrite)).join("")}`
    : `<div class="empty">Nobody is waiting on a person right now.</div>`;

  if (closedHost) {
    closedHost.hidden = !closed.length;
    closedHost.innerHTML = closed.length
      ? `<div class="card-head">Done today</div>${closed.map(closedRow).join("")}` : "";
  }
  if (addCard) addCard.hidden = !mayWrite;
  wire();
}

function say(id: string, text: string): void {
  const m = document.querySelector<HTMLElement>(`[data-msg="${CSS.escape(id)}"]`);
  if (m) { m.textContent = text; m.hidden = !text; }
}

async function act(id: string, what: "claim" | "release" | "close", body: unknown, btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true;
  say(id, "");
  try {
    const { ok, out } = await call(`/api/queue/${encodeURIComponent(id)}/${what}`, body);
    if (!ok) { say(id, out.error ?? "That did not save."); btn.disabled = false; return; }
    await draw();
  } catch {
    say(id, "Could not reach the server. Try again.");
    btn.disabled = false;
  }
}

function wire(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-claim]").forEach((b) =>
    b.addEventListener("click", () => void act(b.dataset["claim"]!, "claim", {}, b)));
  document.querySelectorAll<HTMLButtonElement>("[data-release]").forEach((b) =>
    b.addEventListener("click", () => void act(b.dataset["release"]!, "release", {}, b)));
  document.querySelectorAll<HTMLButtonElement>("[data-done]").forEach((b) =>
    b.addEventListener("click", () => {
      const box = document.querySelector<HTMLElement>(`[data-done-box="${CSS.escape(b.dataset["done"]!)}"]`);
      if (box) { box.hidden = false; box.querySelector("input")?.focus(); }
    }));
  document.querySelectorAll<HTMLButtonElement>("[data-cancel]").forEach((b) =>
    b.addEventListener("click", () => {
      const box = document.querySelector<HTMLElement>(`[data-done-box="${CSS.escape(b.dataset["cancel"]!)}"]`);
      if (box) box.hidden = true;
    }));
  document.querySelectorAll<HTMLButtonElement>("[data-close]").forEach((b) =>
    b.addEventListener("click", () => {
      const id = b.dataset["close"]!;
      const note = document.querySelector<HTMLInputElement>(`[data-note="${CSS.escape(id)}"]`)?.value ?? "";
      void act(id, "close", { note }, b);
    }));

  const add = document.getElementById("hq-add") as HTMLButtonElement | null;
  if (add && !add.dataset["wired"]) {
    add.dataset["wired"] = "1";
    add.addEventListener("click", async () => {
      const msg = document.getElementById("hq-add-msg");
      const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";
      const body = { phone: val("hq-phone"), name: val("hq-name"), reason: val("hq-reason"),
        urgent: (document.getElementById("hq-urgent") as HTMLInputElement | null)?.checked === true };
      /* The same rule the server applies, checked here first so a missing
         reason is a sentence under the button rather than a round trip. */
      const checked = parseManual(body);
      if (!checked.ok) { if (msg) { msg.textContent = checked.error; msg.hidden = false; } return; }
      add.disabled = true;
      try {
        const { ok, out } = await call("/api/queue", body);
        if (!ok) { if (msg) { msg.textContent = out.error ?? "That did not save."; msg.hidden = false; } return; }
        if (msg) msg.hidden = true;
        for (const id of ["hq-phone", "hq-name", "hq-reason"]) {
          const el = document.getElementById(id) as HTMLInputElement | null;
          if (el) el.value = "";
        }
        const u = document.getElementById("hq-urgent") as HTMLInputElement | null;
        if (u) u.checked = false;
        await draw();
      } catch {
        if (msg) { msg.textContent = "Could not reach the server. Try again."; msg.hidden = false; }
      } finally { add.disabled = false; }
    });
  }
}
